import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import styles from './style.module.scss'

/**
 * Ein kurzer Konfettiregen — für den Moment, in dem etwas geschafft ist.
 *
 * Bewusst ohne Zusatzpaket: Es sind knapp achtzig Rechtecke, die fallen und
 * sich drehen. Ein Paket dafür wäre mehr Abhängigkeit als Nutzen, und die
 * Farben sollen ohnehin die der Anwendung sein.
 *
 * Gezeichnet wird auf einer Leinwand über der ganzen Seite, die keine Klicks
 * annimmt. Nach knapp drei Sekunden ist alles unten und die Schleife hört von
 * selbst auf — sie läuft nicht im Hintergrund weiter.
 *
 * **`prefers-reduced-motion` schaltet es ganz ab.** Wer Bewegung abbestellt
 * hat, bekommt keinen Regen; die Meldung „Bestanden" daneben sagt dasselbe in
 * Worten.
 */

const FARBEN = ['#008b9c', '#45b07a', '#ef8400', '#00a0af', '#c3edef', '#22404d']
const STUECKE = 80
const DAUER = 2800

export default function Confetti({ ausloeser }) {
	const canvasRef = useRef(null)

	useEffect(() => {
		if (!ausloeser) return undefined

		const bewegungAus = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
		if (bewegungAus) return undefined

		const canvas = canvasRef.current
		if (!canvas) return undefined

		const ctx = canvas.getContext('2d')
		const dpr = window.devicePixelRatio || 1
		const breite = window.innerWidth
		const hoehe = window.innerHeight

		canvas.width = breite * dpr
		canvas.height = hoehe * dpr
		canvas.style.width = `${breite}px`
		canvas.style.height = `${hoehe}px`
		ctx.scale(dpr, dpr)

		// Alle Teilchen starten oben in der Mitte und fliegen auseinander.
		const teilchen = Array.from({ length: STUECKE }, () => ({
			x: breite / 2 + (Math.random() - 0.5) * breite * 0.5,
			y: -20 - Math.random() * hoehe * 0.3,
			vx: (Math.random() - 0.5) * 3.2,
			vy: 2 + Math.random() * 3,
			dreh: Math.random() * Math.PI,
			vDreh: (Math.random() - 0.5) * 0.25,
			b: 6 + Math.random() * 6,
			h: 8 + Math.random() * 8,
			farbe: FARBEN[Math.floor(Math.random() * FARBEN.length)]
		}))

		let laeuft = true
		const beginn = performance.now()

		const zeichne = jetzt => {
			if (!laeuft) return

			const verstrichen = jetzt - beginn
			ctx.clearRect(0, 0, breite, hoehe)

			// Gegen Ende ausblenden, damit nichts abrupt verschwindet.
			ctx.globalAlpha = Math.max(0, Math.min(1, (DAUER - verstrichen) / 700))

			teilchen.forEach(t => {
				t.x += t.vx
				t.y += t.vy
				t.vy += 0.05 // Schwerkraft
				t.dreh += t.vDreh

				ctx.save()
				ctx.translate(t.x, t.y)
				ctx.rotate(t.dreh)
				ctx.fillStyle = t.farbe
				ctx.fillRect(-t.b / 2, -t.h / 2, t.b, t.h)
				ctx.restore()
			})

			if (verstrichen < DAUER) {
				window.requestAnimationFrame(zeichne)
			} else {
				ctx.clearRect(0, 0, breite, hoehe)
			}
		}

		window.requestAnimationFrame(zeichne)

		return () => {
			laeuft = false
			ctx.clearRect(0, 0, breite, hoehe)
		}
	}, [ausloeser])

	if (!ausloeser) return null

	return <canvas ref={canvasRef} className={styles.root} aria-hidden="true" />
}

Confetti.propTypes = {
	/*
	 * Wechselt der Wert, faellt neues Konfetti. Deshalb eine Zahl und kein
	 * `true`/`false`: Zweimal bestehen soll zweimal Konfetti geben, und ein
	 * gleichbleibendes `true` loeste kein zweites Mal aus.
	 */
	ausloeser: PropTypes.number
}

Confetti.defaultProps = {
	ausloeser: 0
}
